import { Episode, EpisodesManifest } from '../types/Episode';
import matter from 'gray-matter';
import { marked, Renderer } from 'marked';

/**
 * Episodes Loader Service
 *
 * Handles loading episode metadata and content from:
 * - Development: Runtime loading via manifest + fetch
 * - Production: Static imports (handled separately)
 */

// In-memory cache for manifest
let manifestCache: EpisodesManifest | null = null;
let cacheTimestamp: number | null = null;

const CACHE_TTL = 5000; // 5 seconds cache in dev mode

/**
 * Configure custom marked renderer for terminal blocks
 */
const renderer = new Renderer();

// Override code block rendering to handle terminal blocks specially
renderer.code = function(code, language) {
  if (language === 'terminal') {
    // Escape HTML in code content
    const escaped = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // Return a div with data attribute that will be hydrated with React component
    return `<div class="terminal-block" data-terminal-content="${escaped}"></div>`;
  }

  // Default code block rendering for other languages
  return `<pre><code class="language-${language || 'plaintext'}">${code}</code></pre>`;
};

/**
 * Fetch episodes manifest
 */
export async function fetchEpisodesManifest(): Promise<EpisodesManifest> {
  // Check cache (only in dev mode with short TTL)
  if (
    import.meta.env.DEV &&
    manifestCache &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_TTL
  ) {
    return manifestCache;
  }

  try {
    const response = await fetch('/episodes-manifest.json?' + Date.now());

    if (!response.ok) {
      throw new Error(`Failed to fetch episodes manifest: ${response.statusText}`);
    }

    const manifest: EpisodesManifest = await response.json();

    // Update cache
    manifestCache = manifest;
    cacheTimestamp = Date.now();

    return manifest;
  } catch (error) {
    console.error('[episodesLoader] Error fetching manifest:', error);
    throw error;
  }
}

/**
 * Get all episodes (metadata only, no content)
 */
export async function getEpisodes(): Promise<Episode[]> {
  try {
    const manifest = await fetchEpisodesManifest();
    return manifest.episodes;
  } catch (error) {
    console.error('[episodesLoader] Error loading episodes:', error);
    return [];
  }
}

/**
 * Get featured episodes only
 */
export async function getFeaturedEpisodes(): Promise<Episode[]> {
  const episodes = await getEpisodes();
  return episodes.filter(ep => ep.featured);
}

/**
 * Get single episode by slug (includes full content)
 */
export async function getEpisodeBySlug(slug: string): Promise<Episode | null> {
  try {
    console.log(`[episodesLoader] Loading episode: ${slug}`);
    const episodes = await getEpisodes();
    console.log(`[episodesLoader] Total episodes available: ${episodes.length}`);

    const episode = episodes.find(ep => ep.slug === slug);

    if (!episode) {
      console.error(`[episodesLoader] Episode not found in manifest: ${slug}`);
      return null;
    }

    console.log(`[episodesLoader] Found episode in manifest:`, episode.title);

    // If content already loaded, return cached version
    if (episode.content) {
      console.log(`[episodesLoader] Returning cached content`);
      return episode;
    }

    // Fetch full markdown content
    const filename = episode.filename || `${slug}.md`;
    console.log(`[episodesLoader] Fetching markdown file: /posts/${filename}`);
    const response = await fetch(`/posts/${filename}`);

    if (!response.ok) {
      console.error(`[episodesLoader] Failed to fetch: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch episode content: ${response.statusText}`);
    }

    console.log(`[episodesLoader] Markdown fetched successfully, parsing...`);
    const markdown = await response.text();

    // Parse frontmatter and content
    const { content } = matter(markdown);
    console.log(`[episodesLoader] Markdown parsed, content length: ${content.length} chars`);

    // Parse markdown to HTML with custom renderer
    const htmlContent = marked(content, {
      breaks: true,
      gfm: true,
      renderer: renderer
    });

    console.log(`[episodesLoader] HTML generated, length: ${(htmlContent as string).length} chars`);

    // Return episode with content
    return {
      ...episode,
      content: htmlContent as string
    };
  } catch (error) {
    console.error(`[episodesLoader] Error loading episode "${slug}":`, error);
    return null;
  }
}

/**
 * Search episodes by query
 */
export async function searchEpisodes(query: string): Promise<Episode[]> {
  const episodes = await getEpisodes();
  const lowerQuery = query.toLowerCase();

  return episodes.filter(episode =>
    episode.title.toLowerCase().includes(lowerQuery) ||
    episode.excerpt.toLowerCase().includes(lowerQuery) ||
    episode.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Filter episodes by tag
 */
export async function getEpisodesByTag(tag: string): Promise<Episode[]> {
  const episodes = await getEpisodes();
  return episodes.filter(episode => episode.tags.includes(tag));
}

/**
 * Get all unique tags from episodes
 */
export async function getAllTags(): Promise<string[]> {
  const episodes = await getEpisodes();
  const tagsSet = new Set<string>();

  episodes.forEach(episode => {
    episode.tags.forEach(tag => tagsSet.add(tag));
  });

  return Array.from(tagsSet).sort();
}

/**
 * Clear cache (useful for manual refresh)
 */
export function clearEpisodesCache(): void {
  // Cache cleared
  manifestCache = null;
  cacheTimestamp = null;
}

/**
 * Listen for HMR events to refresh episodes
 */
if (import.meta.hot) {
  import.meta.hot.on('episodes-updated', () => {
    console.log('[episodesLoader] Episodes updated, clearing cache');
    clearEpisodesCache();
  });
}
